/**
 * CxL3 — UPS.
 *
 * Mode transfers are the heart of it. The failure this catalogue is shaped to
 * catch is the one that only shows up on transfer: a UPS that runs happily on
 * mains, runs happily on battery, and drops the load in the half-second between
 * them because a bypass static switch was never proved.
 *
 * **A full autonomy discharge is CxL4** and is not here. It needs a load, a
 * measured runtime and a recharge window, and it is a different day's work.
 *
 * Draft defaults. Replace with the CxA's issued UPS script when it arrives.
 */

import type { CatalogueTest } from '../catalogue.js';

export const CXL3_UPS: readonly CatalogueTest[] = [
  {
    code: 'UPS-VIS',
    name: 'Visual inspection and readiness',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Visual Inspection',
    checks: [
      { label: 'Rating plate matches the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Input, output and bypass cables terminated and labelled', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Battery cabinet or rack secure, links torqued and covered', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Earthing and bonding of UPS and battery frame', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Ventilation and room cooling adequate, filters clean', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Maintenance bypass procedure displayed at the unit', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['UPS-VIS-PLATE', 'UPS-VIS-CABLE', 'UPS-VIS-MBP'],
  },
  {
    code: 'UPS-VT',
    name: 'Input and output measurements',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Voltage Test',
    checks: [
      { label: 'Input L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Input L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Input L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'Output L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 2, toleranceMode: 'percent' },
      { label: 'Output L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 2, toleranceMode: 'percent' },
      { label: 'Output L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 2, toleranceMode: 'percent' },
      { label: 'Output frequency', responseType: 'measured_pass_fail', unit: 'Hz', nominal: 50, tolerance: 1, toleranceMode: 'percent' },
      { label: 'Input and output phase rotation agree', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['UPS-VT-INPUT', 'UPS-VT-OUTPUT', 'UPS-PR-METER'],
  },
  {
    code: 'UPS-MODE',
    name: 'Mode transfers',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Every transfer is judged by whether the load stayed up, not by what the display said afterwards.',
    checks: [
      { label: 'Normal → battery on simulated mains failure', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Transfers to battery with no break; ON BATTERY indicated and alarmed' },
      { label: 'Battery → normal on mains restoration', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Returns to normal; rectifier walks in and charging begins' },
      { label: 'Normal → static bypass', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Transfers with no break; ON BYPASS indicated and alarmed' },
      { label: 'Static bypass → normal', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Returns to inverter with no break' },
      { label: 'Transfer to maintenance bypass, following the displayed procedure', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Load carried by maintenance bypass; UPS isolated and safe to work on' },
      { label: 'Return from maintenance bypass', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'UPS retakes the load with no break' },
      { label: 'Output remained energised throughout every transfer', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'No loss of output at any point' },
    ],
    shotCodes: ['UPS-MODE-BATT', 'UPS-MODE-BYPASS', 'UPS-MODE-NORMAL'],
  },
  {
    code: 'UPS-BATT',
    name: 'Battery and charging',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Block voltages recorded per string. An outlier here is the block that fails the CxL4 discharge.',
    checks: [
      { label: 'String voltage on float', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Block / cell voltages within the manufacturer spread', responseType: 'per_circuit', unit: 'V', toleranceMode: 'none', acceptanceCriteria: 'No block outside the manufacturer band' },
      { label: 'Charger float voltage matches the manufacturer figure', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'Battery breaker / fuse operates and indicates', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'UPS raises battery-isolated alarm; load unaffected' },
      { label: 'Battery low alarm', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Alarm locally and at EPMS/BMS' },
      { label: 'Battery room ventilation and temperature monitoring operating', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['UPS-BATT-STRING', 'UPS-BATT-BLOCK', 'UPS-BATT-CHG'],
  },
  {
    code: 'UPS-ALARM',
    name: 'Alarms and EPMS / BMS points',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Functional Check with EPMS/BMS',
    checks: [
      { label: 'Mains fail alarm', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'On battery point', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'On bypass point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Battery low point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Overload point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Fan / cooling fail point', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Common fault point', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['UPS-BMS-SCREEN'],
  },
  {
    code: 'UPS-MV',
    name: 'Meter verification — UPS display against reference',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    note: 'Three sources, as on the switchboards: the unit display, the PQA and the EPMS/BMS value.',
    checks: [
      { label: 'Output voltage', responseType: 'meter_verification', unit: 'V', tolerance: 1, toleranceMode: 'percent' },
      { label: 'Output current', responseType: 'meter_verification', unit: 'A', tolerance: 2, toleranceMode: 'percent' },
      { label: 'Output power', responseType: 'meter_verification', unit: 'kW', tolerance: 3, toleranceMode: 'percent' },
      { label: 'Load percentage', responseType: 'meter_verification', unit: '%', tolerance: 3, toleranceMode: 'percent' },
    ],
    shotCodes: ['UPS-MV-DISPLAY', 'UPS-MV-PQA', 'UPS-MV-BMS'],
  },
  {
    code: 'UPS-PARALLEL',
    name: 'Parallel and redundancy operation',
    cxLevel: 'CxL3',
    equipmentTypes: ['UPS'],
    scopes: ['unit'],
    defaultSelected: false,
    section: 'Functional Test',
    note: 'Off by default. Only applies to a parallel or N+1 arrangement, and only once every module is complete.',
    checks: [
      { label: 'Load shares within the manufacturer tolerance across modules', responseType: 'measured_pass_fail', unit: '%', tolerance: 5, toleranceMode: 'max' },
      { label: 'One module removed — remaining modules carry the load', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'No loss of output; redundancy-lost alarm raised' },
      { label: 'Module returned — load resharing without disturbance', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Module rejoins and takes its share' },
    ],
    shotCodes: ['UPS-PAR-SHARE', 'UPS-PAR-ALARM'],
  },
];
